import React, { useState, useEffect } from 'react';
import { usersApi, entitiesApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Trash2, Shield, ShieldCheck, Eye, UserCog, Settings2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full control', icon: ShieldCheck },
  { value: 'manager', label: 'Manager', desc: 'Full control except user management', icon: Shield },
  { value: 'partner', label: 'Partner', desc: 'View own data only', icon: Eye },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only access', icon: Eye },
];

const ALL_PAGES = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'sites', label: 'Sites' },
  { value: 'entries', label: 'Entries' },
  { value: 'transfers', label: 'Transfers' },
  { value: 'ledger', label: 'Ledger' },
  { value: 'entities', label: 'Entities' },
  { value: 'partners', label: 'Partners' },
  { value: 'cash-bank', label: 'Cash & Bank' },
];

const ROLE_COLORS = {
  admin: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400',
  manager: 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400',
  partner: 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400',
  viewer: 'bg-zinc-100 dark:bg-zinc-800/50 text-zinc-700 dark:text-zinc-400',
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAccess, setShowAccess] = useState(null); // user object
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'viewer', entity_id: '' });
  const [accessPages, setAccessPages] = useState([]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await usersApi.list();
      setUsers(data);
    } catch (err) { toast.error('Failed to load users'); }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
    entitiesApi.list('partner').then(r => setPartners(r.data)).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (form.role === 'partner') {
        payload.allowed_pages = ['dashboard', 'ledger'];
      }
      await usersApi.create(payload);
      setShowForm(false);
      setForm({ email: '', password: '', name: '', role: 'viewer', entity_id: '' });
      fetchUsers();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create user'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user account permanently?')) return;
    try {
      await usersApi.delete(id);
      fetchUsers();
    } catch (err) { toast.error('Failed to delete user'); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersApi.updateRole(userId, { role: newRole });
      fetchUsers();
    } catch (err) { toast.error('Failed to update role'); }
  };

  const openAccessPanel = (u) => {
    setShowAccess(u);
    setAccessPages(u.allowed_pages || []);
  };

  const togglePage = (page) => {
    setAccessPages(prev =>
      prev.includes(page) ? prev.filter(p => p !== page) : [...prev, page]
    );
  };

  const saveAccess = async () => {
    if (!showAccess) return;
    try {
      await usersApi.updateAccess(showAccess._id, { allowed_pages: accessPages });
      setShowAccess(null);
      fetchUsers();
    } catch (err) { toast.error('Failed to update access'); }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">User Accounts</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage admin, manager, partner & viewer accounts</p>
        </div>
        <Button className="gap-1.5" onClick={() => { setForm({ email: '', password: '', name: '', role: 'viewer', entity_id: '' }); setShowForm(true); }} data-testid="create-user-btn">
          <Plus className="w-4 h-4" /> Create Account
        </Button>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="bg-card border border-border overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Name</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Email</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Role</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Access</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u._id} className="border-b border-border hover:bg-muted/50 transition-colors" data-testid={`user-row-${u._id}`}>
                  <td className="py-3 px-4 font-medium">{u.name || '-'}</td>
                  <td className="py-3 px-4 text-muted-foreground">{u.email}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-medium px-2 py-0.5 capitalize ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs text-muted-foreground">
                      {u.role === 'admin' || u.role === 'manager'
                        ? 'Full access'
                        : `${(u.allowed_pages || []).length} pages`}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    {u._id !== currentUser?._id && (
                      <>
                        {(u.role === 'partner' || u.role === 'viewer') && (
                          <button onClick={() => openAccessPanel(u)} className="p-1.5 hover:bg-muted rounded" title="Manage Access" data-testid={`access-user-${u._id}`}>
                            <Settings2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <Select value={u.role} onValueChange={v => handleRoleChange(u._id, v)}>
                          <SelectTrigger className="inline-flex w-auto h-7 text-xs gap-1 px-2 border-0 bg-transparent hover:bg-muted" data-testid={`role-select-${u._id}`}>
                            <UserCog className="w-3 h-3" />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map(r => (
                              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <button onClick={() => handleDelete(u._id)} className="p-1.5 hover:bg-muted rounded text-red-600" title="Delete" data-testid={`delete-user-${u._id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    {u._id === currentUser?._id && (
                      <span className="text-xs text-muted-foreground italic">You</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create User Account</DialogTitle>
            <DialogDescription className="sr-only">Create a new user account with role</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="create-user-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="mt-1.5" data-testid="user-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required className="mt-1.5" data-testid="user-email-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Password</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required className="mt-1.5" data-testid="user-password-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      <div className="flex items-center gap-2">
                        <r.icon className="w-3.5 h-3.5" />
                        <span>{r.label}</span>
                        <span className="text-xs text-muted-foreground">— {r.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.role === 'partner' && (
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Link to Partner Entity</Label>
                <Select value={form.entity_id} onValueChange={v => setForm(f => ({ ...f, entity_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="user-entity-select"><SelectValue placeholder="Select partner entity" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {partners.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Links this account to a partner entity for data scoping</p>
              </div>
            )}
            <Button type="submit" className="w-full" data-testid="submit-create-user-btn">Create Account</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Access Management Dialog */}
      <Dialog open={!!showAccess} onOpenChange={() => setShowAccess(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Page Access — {showAccess?.name}</DialogTitle>
            <DialogDescription className="sr-only">Configure which pages this user can access</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="access-dialog">
            <p className="text-sm text-muted-foreground">
              Select which pages <strong>{showAccess?.name}</strong> ({showAccess?.role}) can access:
            </p>
            <div className="space-y-2">
              {ALL_PAGES.map(pg => (
                <div key={pg.value} className="flex items-center gap-3 p-2 hover:bg-muted rounded transition-colors">
                  <Checkbox
                    id={`page-${pg.value}`}
                    checked={accessPages.includes(pg.value)}
                    onCheckedChange={() => togglePage(pg.value)}
                    data-testid={`toggle-page-${pg.value}`}
                  />
                  <label htmlFor={`page-${pg.value}`} className="text-sm cursor-pointer flex-1">{pg.label}</label>
                </div>
              ))}
            </div>
            <Button onClick={saveAccess} className="w-full" data-testid="save-access-btn">Save Access</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
